import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';
import { mockOptimizationData } from '../../data/teaserMockData';

interface SmartCapitalReallocationDemoProps extends TeaserDemoProps {
  allocations?: typeof mockOptimizationData.allocations;
  totalCapital?: string;
}

const SmartCapitalReallocationDemoComponent: React.FC<SmartCapitalReallocationDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  allocations = mockOptimizationData.allocations.slice(0, 3), // Show top 3 allocations
  totalCapital = mockOptimizationData.metrics.totalCapital
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center"
          >
            <ArrowTrendingUpIcon className="w-3 h-3 text-white" />
          </motion.div>
          <div>
            <h4 className="text-white font-semibold text-sm">Smart Reallocation</h4>
            <p className="text-gray-300 text-xs">Capital: {totalCapital}</p>
          </div>
        </div>
      </div>

      {/* Allocation Cards */}
      <div className="space-y-2">
        {allocations.map((alloc, index) => (
          <motion.div
            key={alloc.strategy}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.3, duration: 0.5 }}
            className="group"
          >
            <motion.div
              animate={{ 
                borderColor: ['rgba(34, 197, 94, 0.4)', 'rgba(34, 197, 94, 0.7)', 'rgba(34, 197, 94, 0.4)']
              }}
              transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.5 }}
              className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-3 backdrop-blur-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowUpIcon className="w-4 h-4 text-green-400" />
                  <div>
                    <p className="text-white font-medium text-sm">{alloc.strategy}</p>
                    <p className="text-gray-300 text-xs">{alloc.account}</p>
                  </div>
                </div>
                <div className="text-right">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.3 }}
                    className="text-green-400 font-bold text-sm"
                  >
                    {alloc.performance}
                  </motion.div>
                  <p className="text-xs text-gray-400">{alloc.allocation}%</p>
                </div>
              </div>

              {/* Allocation Bar */}
              <div className="mt-2 bg-black/20 rounded-full h-1.5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${alloc.allocation}%` }}
                  transition={{ delay: index * 0.3 + 0.5, duration: 1.5, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
                />
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Performance Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="mt-4 text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-blue-300 text-xs font-medium"
        >
          💎 Optimized for maximum returns
        </motion.p>
        <p className="text-white/60 text-xs mt-1">
          Sharpe Ratio: {mockOptimizationData.metrics.sharpeRatio}
        </p>
      </motion.div>
    </motion.div>
  );
};

export const SmartCapitalReallocationDemo = createOptimizedTeaserDemo(
  SmartCapitalReallocationDemoComponent,
  'SmartCapitalReallocationDemo'
);